import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, AlertTriangle } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import api from "../lib/api";

const MAX_ATTEMPTS = 10;
const INTERVAL_MS = 2000;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState({ state: "checking", payment_status: null, amount_total: 0, currency: "usd" });
  const attemptsRef = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setStatus({ state: "error", message: "No session ID found." });
      return;
    }
    let cancelled = false;

    const poll = async () => {
      if (cancelled) return;
      attemptsRef.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setStatus({ state: "paid", payment_status: "paid", amount_total: data.amount_total, currency: data.currency });
          return;
        }
        if (data.status === "expired") {
          setStatus({ state: "expired" });
          return;
        }
        if (attemptsRef.current >= MAX_ATTEMPTS) {
          setStatus({ state: "timeout" });
          return;
        }
        setStatus({ state: "pending" });
        setTimeout(poll, INTERVAL_MS);
      } catch {
        if (attemptsRef.current >= MAX_ATTEMPTS) {
          setStatus({ state: "error", message: "Unable to verify payment." });
          return;
        }
        setTimeout(poll, INTERVAL_MS);
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  return (
    <div data-testid="payment-success-page">
      <Navbar />
      <section className="max-w-3xl mx-auto px-6 lg:px-10 pt-32 pb-24 text-center">
        {status.state === "checking" || status.state === "pending" ? (
          <div className="glass rounded-sm p-12" data-testid="payment-pending">
            <Loader2 className="w-12 h-12 text-[var(--pov-cyan)] mx-auto mb-6 animate-spin" />
            <p className="font-display text-3xl">Confirming your booking…</p>
            <p className="text-white/60 mt-3">A moment, while Stripe and our practice shake hands.</p>
          </div>
        ) : status.state === "paid" ? (
          <div className="glass rounded-sm p-12" data-testid="payment-paid">
            <CheckCircle2 className="w-14 h-14 text-[var(--pov-cyan)] mx-auto mb-6" />
            <p className="overline">Booking Confirmed</p>
            <p className="font-display text-4xl mt-3">Thank you.</p>
            <p className="text-white/70 mt-4 max-w-md mx-auto">
              We have received your payment of ${(status.amount_total / 100).toFixed(2)} {status.currency.toUpperCase()}.
              The lead consultant will reach out personally to schedule your session.
            </p>
            <Link to="/" className="btn-secondary mt-8 inline-flex" data-testid="payment-home-btn">Return Home</Link>
          </div>
        ) : (
          <div className="glass rounded-sm p-12" data-testid="payment-issue">
            <AlertTriangle className="w-12 h-12 text-[var(--pov-coral)] mx-auto mb-6" />
            <p className="font-display text-3xl">We could not confirm the payment.</p>
            <p className="text-white/65 mt-3">{status.message || "If you were charged, please contact us and we will resolve immediately."}</p>
            <div className="mt-8 flex justify-center gap-3">
              <Link to="/book" className="btn-primary" data-testid="payment-retry-btn">Try Again</Link>
              <Link to="/" className="btn-secondary" data-testid="payment-home-btn-2">Return Home</Link>
            </div>
          </div>
        )}
      </section>
      <Footer />
    </div>
  );
}
